<template>
  <BaseLayout>
    <BaseDashboard :is-loading="isLoading">
      <div class="d-flex flex-column justify-content-between h-100 overflow-hidden">
        <div class="page-header px-5 py-3">
          <b-row class="full-width">
            <b-col cols="3">
              <b-form-group
                id="campaign-search-group"
                class="m-0 p-0"
              >
                <template
                  slot="label"
                  label-for="campaign-search-group"
                  class="form-label"
                >
                  Campaign search
                </template>
                <base-autocomplete
                  id="search-campaign"
                  class=""
                  data-test="campaign-name-search-input"
                  size="sm"
                  :items="filteredCampaignOptions"
                  v-model="campaignSearch"
                  :search-text.sync="campaignSearch"
                  @change="onFilterChange"
                  @keyup.native="onKeyUp"
                  @keyup.native.enter="onCampaignSearchEnter"
                />
              </b-form-group>
            </b-col>
            <b-col cols="8">
              <b-row
                class="ml-3"
                v-if="isManagedCampaigns"
              >
                <b-col cols="12">
                  <div class="form-label pb-1">
                    Filters
                  </div>
                </b-col>
                <b-col cols="4">
                  <base-filter-dropdown
                    class="w-100 custom-height-dropdown"
                    :filter="'States'"
                    :options-list="stateOptions"
                    :key-property="'id'"
                    :value-property="'name'"
                    :selected-options="statesFilters"
                    @updatedSelectedOptions="selectStatesFilter(...arguments)"
                    @selectAllOptions="selectAllStates"
                    @deselectAllOptions="deselectAllStates"
                  />
                </b-col>
                <b-col cols="4">
                  <b-form-select
                    v-model="advertiserFilter"
                    :options="advertiserOptions"
                    size="sm"
                    data-test="advertiser-filter-select"
                    @change="onFilterChange"
                  />
                </b-col>
                <b-col cols="4">
                  <b-form-select
                    v-if="isMyDashboard"
                    v-model="accountManagerFilter"
                    :options="accountManagerOptions"
                    size="sm"
                    data-test="account-manager-filter-select"
                    @change="onFilterChange"
                  />
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </div>
        <div class="flex-grow-1 overflow-auto">
          <div
            class="h-100 table-responsive mb-0 mt-3"
            v-if="!isComponentLoading"
          >
            <b-table
              ref="campaigns"
              class="table-vcenter text-nowrap"
              :class="{ 'h-100': campaignGroups.length === 0 }"
              tbody-tr-class="campaign-table-row"
              details-td-class="p-0"
              :striped="false"
              :bordered="false"
              :outlined="false"
              :hover="true"
              :items="campaignGroups"
              :fields="getHeaders"
              :busy.sync="isBusy"
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
              :no-local-sorting="true"
              show-empty
              data-test="campaigns-list"
            >
              <template v-slot:table-colgroup="scope">
                <col
                  v-for="field in scope.fields"
                  :key="field.key"
                  :style="{ width: field.width ? field.width : 'auto' }"
                >
              </template>
              <template #cell(expand)="data">
                <div
                  ref="expand-column"
                  id="expand-column"
                  class="mb-0 mr-0 ml-0"
                >
                  <div
                    v-if="data.item.hasCustomGroup"
                  >
                    <i
                      class="far"
                      :class="data.detailsShowing ? 'fa-chevron-down' : 'fa-chevron-right'"
                      @click="data.toggleDetails"
                    />
                  </div>
                </div>
              </template>
              <template v-slot:cell(name)="data">
                <div
                  ref="campaign-name-column"
                >
                  <span
                    v-if="data.item.hasCustomGroup"
                    class="ellipsis"
                    data-test="campaign-title"
                  >
                    {{ data.value }}
                  </span>
                  <span
                    class="ellipsis"
                    data-test="campaign-title"
                    v-else
                  >
                    <router-link
                      v-if="isDraftState(data.item)"
                      data-test="campaign-details-link"
                      class="large"
                      :to="{
                        name: 'NewCampaign',
                        params: {
                          campaignId: data.item.campaigns[0].id,
                          advertiserId: data.item.advertiserId
                        }
                      }"
                    >
                      {{ data.value }}
                    </router-link>
                    <router-link
                      v-else-if="isEndedState(data.item)"
                      class="large"
                      data-test="campaign-view-link"
                      :to="{
                        name: 'ViewCampaign',
                        params: {
                          campaignId: data.item.campaigns[0].id,
                          advertiserId: data.item.advertiserId
                        }
                      }"
                    >
                      {{ data.value }}
                    </router-link>
                    <feature
                      name="CAMPAIGN_EDIT"
                      v-else-if="canEdit(data.item)"
                    >
                      <router-link
                        class="large"
                        data-test="campaign-edit-link"
                        :to="{ name: 'EditCampaign',
                               params: {
                                 campaignId: data.item.campaigns[0].id,
                                 advertiserId: data.item.advertiserId
                               }
                        }"
                      >
                        {{ data.value }}
                      </router-link>
                    </feature>
                  </span>
                  <div
                    class="small text-muted"
                    title="Advertiser Name"
                    data-test="campaign-advertiser-name"
                  >
                    {{ getAdvertiserName(data.item.advertiserId) }}
                  </div>
                </div>
              </template>
              <template v-slot:cell(state)="data">
                <div
                  ref="state-column"
                  v-if="data.item.state"
                >
                  <span
                    :data-test="`campaign-status-${data.value}`"
                  >
                    <span :class="[getStatusIcon(data), getStatusColor(data)]" />
                    {{ getColumnState(data) }}
                  </span>

                  <span
                    v-if="!data.item.hasCustomGroup"
                  >
                    <a
                      v-if="data.item.state === 'LIVE' && isMyDashboard"
                      href="#"
                      class="icon ml-3"
                      data-test="campaign-status-pause"
                      @click="updateCampaignState(data.item, 'PAUSED')"
                    >
                      <i class="fa-light fa-pause" />
                    </a>
                    <a
                      v-if="data.item.state === 'PAUSED' && isMyDashboard"
                      href="#"
                      class="icon ml-3"
                      data-test="campaign-status-start"
                      @click="updateCampaignState(data.item, 'LIVE')"
                    >
                      <i class="fa-light fa-play" />
                    </a>
                  </span>
                </div>
              </template>
              <template v-slot:cell(startDate)="data">
                <div
                  ref="start-date-column"
                >
                  <small>{{ formatDateForPlatform(data.value) }}</small>
                </div>
              </template>
              <template v-slot:cell(endDate)="data">
                <div
                  ref="end-date-column"
                >
                  <small>{{ formatDateForPlatform(data.value) }}</small>
                </div>
              </template>
              <template v-slot:cell(budget)="data">
                <div
                  ref="budget-column"
                >
                  <div class="clearfix">
                    <div
                      class="float-left"
                      data-test="budget-spent-percentage"
                    >
                      <strong>{{ getBudgetSpentPercentage(data.item.budgetRemaining) || '0' }}%</strong>
                    </div>
                    <div class="float-right">
                      <small class="text-muted">{{ formattedMoneyAmount(data.item) }}</small>
                    </div>
                  </div>
                  <div class="progress progress-xs">
                    <div
                      class="progress-bar"
                      :class="setProgressColor(getBudgetSpentPercentage(data.item.budgetRemaining)
                        || 0)"
                      role="progressbar"
                      :style="`width: ${getBudgetSpentPercentage(data.item.budgetRemaining) || '0'}%`"
                      :aria-valuenow="getBudgetSpentPercentage(data.item.budgetRemaining) || '0'"
                      aria-valuemin="0"
                      aria-valuemax="100"
                    />
                  </div>
                </div>
              </template>
              <template v-slot:cell(impressions)="data">
                <div
                  ref="impressions-column"
                >
                  {{ data.value || 0 }}
                </div>
              </template>
              <template v-slot:cell(clicks)="data">
                <div
                  ref="clicks-column"
                >
                  {{ data.value || 0 }}
                </div>
              </template>
              <template v-slot:cell(ctr)="data">
                <div
                  ref="ctr-column"
                >
                  {{ data.value || `%` }}
                </div>
              </template>
              <template
                v-slot:cell(accountManager)="data"
              >
                <div
                  ref="account-manager-column"
                  class="avatar avatar-manager"
                  :class="getAvatarClass(data.item.campaigns[0].accountManagerUserId)"
                >
                  {{ getAvatarInitials(data.item.campaigns[0].accountManagerUserId) }}
                </div>
              </template>
              <template v-slot:cell(action)="data">
                <div
                  ref="actions-column"
                >
                  <div
                    v-if="!data.item.hasCustomGroup"
                  >
                    <CampaignActions
                      :index="data.index"
                      :item="data.item.campaigns[0]"
                      @removeCampaign="removeCampaign"
                      @startOrPauseCampaign="setCampaignState"
                      @setCampaignToDraft="setCampaignToDraft"
                      @refreshTable="refreshTable"
                      @setDormantToLive="setCampaignState"
                      :is-my-dashboard="isMyDashboard"
                    />
                  </div>
                  <div v-else>
                    <CampaignGroupActions
                      :index="data.index"
                      :advertiser-id="data.item.advertiserId"
                      :group="data.item.name"
                      :campaigns="data.item.campaigns"
                      :is-my-dashboard="isMyDashboard"
                      @refreshTable="refreshTable"
                    />
                  </div>
                </div>
              </template>
              <template #row-details="{item}">
                <CampaignsTable
                  :is-my-dashboard="isMyDashboard"
                  :campaigns="item.campaigns"
                  :account-managers-map="accountManagersMap"
                  @refreshTable="refreshTable"
                  :is-managed="isManagedCampaigns"
                />
              </template>
              <template v-slot:table-busy>
                <div class="text-center my-2">
                  <div class="loader mx-auto" />
                  <h6>Campaigns Loading</h6>
                </div>
              </template>
              <template v-slot:empty>
                <div class="row justify-content-md-center py-5">
                  <div
                    class="text-center"
                    v-if="isNotSearching"
                  >
                    <div>
                      <p
                        class="font-weight-bold intro-message text-center"
                      >
                        Ready to kickstart your contextual journey?<br>Create your first campaign now!
                      </p>
                      <router-link
                        to="/new-campaign"
                        class="btn btn-primary mt-3"
                        data-test="table-new-campaign-button"
                      >
                        New Campaign
                      </router-link>
                    </div>
                  </div>
                  <div
                    class="col-4 text-center"
                    v-else
                  >
                    <h4>Sorry, you don't seem to have any campaigns that match your search.</h4>
                  </div>
                </div>
              </template>
            </b-table>
          </div>
        </div>
        <div class="bg-secondary border-top pt-4">
          <base-pagination
            :items-count="groupCount"
            :current-page.sync="currentPage"
            :per-page-selected.sync="perPageSelected"
            items-name="campaign groups"
            data-test="campaign-pagination"
            @pagingUpdate="updatePaging(...arguments)"
          />
        </div>
      </div>
    </BaseDashboard>
  </BaseLayout>
</template>

<script>
import {
  mapActions, mapState, mapMutations, mapGetters,
} from 'vuex';
import * as types from '@/store/types';
import * as campaignTypes from '@/modules/campaigns/store/types';
import * as accountTypes from '@/modules/account/store/types';

import currenciesMixin from '@/core/mixins/currencies';
import paginationMixin from '@/core/mixins/pagination';
import dateHelper from '@/core/mixins/dateHelper';
import tableMixin from '@/modules/campaigns/mixins/tableMixin';

import helper from '@/core/helper';
import urlHelper from '@/core/mixins/urlHelper';
import tableHeaders from '@/views/Campaign/tableHeaders';
import CampaignActions from '@/modules/campaigns/components/CampaignActions';
import CampaignGroupActions from '@/modules/campaigns/components/CampaignGroupActions';
import BasePagination from '@/components/BasePagination.vue';
import BaseAutocomplete from '@/components/form/BaseAutocomplete.vue';
import BaseFilterDropdown from '@/components/BaseFilterDropdown.vue';
import CampaignsTable from './CampaignsTable.vue';

export default {
  name: 'Dashboard',
  mixins: [
    currenciesMixin,
    paginationMixin,
    urlHelper,
    dateHelper,
    tableMixin,
  ],
  components: {
    CampaignsTable,
    BaseFilterDropdown,
    BaseAutocomplete,
    BasePagination,
    CampaignActions,
    CampaignGroupActions,
  },
  data() {
    return {
      ...tableHeaders,
      isBusy: false,
      campaignList: [],
      campaignGroups: [],
      pageOffset: 1,
      currentPage: 1,
      perPageSelected: 10,
      itemsCount: 0,
      groupCount: 0,
      sortBy: '',
      sortDesc: false,
      campaignSearchName: '',
      campaignSearch: '',
      filteredCampaignOptions: [],
      advertiserFilter: null,
      accountManagerFilter: null,
      statesFilters: [],
      defaultStatesFilters: ['DRAFT', 'REVIEW', 'LIVE', 'PAUSED', 'DORMANT'],
      stateOptions: [],
      advertiserOptions: [{ value: null, text: 'Advertiser filter' }],
      accountManagersMap: new Map(),
      accountManagerOptions: [{ value: null, text: 'Account Manager Filter' }],
      isMyDashboard: true,
      isManagedCampaigns: false,
      isComponentLoading: true,
    };
  },
  methods: {
    ...mapActions('account', [
      accountTypes.FETCH_ALL_USERS_IN_ROLE,
      accountTypes.FETCH_ACCOUNT,
    ]),
    ...mapActions('campaigns', [
      campaignTypes.FETCH_EXISTING_ADVERTISERS,
      campaignTypes.FETCH_CURRENCIES,
      campaignTypes.FETCH_CAMPAIGN_GROUPS_WITH_CAMPAIGNS,
      campaignTypes.FETCH_CAMPAIGN_GROUPS,
      campaignTypes.REMOVE_CAMPAIGN,
      campaignTypes.SET_CAMPAIGN_STATE,
      campaignTypes.FETCH_CAMPAIGN_STATES,
    ]),
    ...mapMutations('campaigns', [
      campaignTypes.SET_IS_LOADING,
    ]),
    ...mapMutations([
      types.SET_GLOBAL_MESSAGE,
    ]),
    setCampaignToDraft(item) {
      this.setCampaignState(item, 'DRAFT', !this.isMyDashboard)
    },
    setCampaignState(item, state) {
      this.updateCampaignState(item, state);
    },
    removeCampaign(item) {
      this.REMOVE_CAMPAIGN({
        advertiserId: item.advertiserId,
        campaignId: item.id,
        campaignName: item.name,
      })
        .finally(() => {
          this.getAllCampaigns();
        }).catch(() => {
          this.getAllCampaigns();
        });
    },
    async getAllCampaigns() {
      this.isBusy = true;

      const account = await this.FETCH_ACCOUNT();
      if (this.isNewAccount(account)) {
        return [];
      }

      const params = {
        page: this.currentPage,
        perPage: this.perPageSelected,
        sortBy: this.sortBy,
        sortDesc: this.sortDesc,
        type: this.isMyDashboard ? 'MANAGED_NETWORK' : 'SELF_SERVICE',
      };

      const response = await this.FETCH_CAMPAIGN_GROUPS_WITH_CAMPAIGNS({ params, filters: this.filters, managed: this.GET_IS_MANAGED_ACCOUNT });
      this.campaignGroups = response[0];
      this.attachResizeObserver();
      this.groupCount = response[1];
      this.isBusy = false;
    },
    isNewAccount(account) {
      return account.firstName === null && account.advertisers.length === 0;
    },
    getAdvertiserName(id) {
      const advertiser = this.existingAdvertisers.find((item) => (item.id === id));
      if (advertiser && advertiser.name) {
        return advertiser.name;
      }
      return '';
    },
    resetPage() {
      this.currentPage = 1;
      this.pageOffset = 1;
    },
    onFilterChange() {
      this.resetPage();
      this.refreshTable();
    },
    async updatePaging(payload) {
      const isSameCurrentPage = payload.page === this.currentPage;
      const isSamePerPageSelected = payload.perPageSelected === this.perPageSelected;
      if (isSameCurrentPage && isSamePerPageSelected) return;
      this.currentPage = payload.page;
      this.perPageSelected = payload.perPageSelected;
    },
    onCampaignSearchEnter() {
      this.resetPage();
      this.refreshTable();
    },
    onKeyUp: helper.debounce(function getCampaignsFiltered() {
      const campaignType = this.isMyDashboard ? null : 'SELF_SERVICE';
      return this.FETCH_CAMPAIGN_GROUPS({ search: this.campaignSearch, campaignType })
        .then((response) => {
          this.filteredCampaignOptions = response;
        });
    }, 500),
    createAdvertisersList() {
      this.advertiserOptions = Object.freeze([this.advertiserOptions[0]].concat(this.existingAdvertisers
        .map((item) => ({ value: item.id, text: item.name }))));
    },
    async createAccountManagerList() {
      const users = await this.FETCH_ALL_USERS_IN_ROLE('ACCOUNT_MANAGER');
      const accountManagers = users ? users.userResponses : [];

      this.accountManagerOptions = Object.freeze([this.accountManagerOptions[0]].concat(accountManagers
        .map((accountManager) => ({
          text: `${accountManager.firstName} ${accountManager.lastName}`,
          value: accountManager.id,
        }))));
      this.accountManagersMap = new Map(accountManagers
        .map((accountManager) => ({
          id: accountManager.id,
          initials: `${accountManager.firstName[0]?.toUpperCase()}${accountManager.lastName[0]?.toUpperCase()}`,
          avatarColour: accountManager.properties?.avatarColour || process.env.VUE_APP_AVATAR_DEFAULT_COLOUR,
        }))
        .map((accountManager) => [accountManager.id, accountManager]));
    },
    selectStatesFilter(id, isChecked) {
      if (isChecked) {
        this.statesFilters.push(id);
      } else {
        const index = this.statesFilters.indexOf(id);
        this.statesFilters.splice(index, 1);
      }
      this.refreshTable();
    },
    selectAllStates() {
      this.statesFilters = this.stateOptions.map((option) => option.id);
      this.refreshTable();
    },
    deselectAllStates() {
      this.statesFilters = [];
      this.refreshTable();
    },
    selectFilters() {
      if (this.isManagedCampaigns) {
        const dashboardFiltersLocal = localStorage.getItem('dashboardFilters');
        const dashboardFilters = dashboardFiltersLocal ? JSON.parse(dashboardFiltersLocal)[this.dashboardName] : undefined;
        if (dashboardFilters) {
          this.advertiserFilter = dashboardFilters.advertiserFilter;
          this.accountManagerFilter = dashboardFilters.accountManagerFilter;
          this.statesFilters = dashboardFilters.statesFilters;
        }
        if (this.statesFilters.length === 0) this.statesFilters = this.defaultStatesFilters;
      }
    },
    saveFilters() {
      if (this.isManagedCampaigns) {
        const dashboardFilters = JSON.parse(localStorage.getItem('dashboardFilters') || '{}');
        dashboardFilters[this.dashboardName] = {
          advertiserFilter: this.advertiserFilter,
          statesFilters: this.statesFilters,
          accountManagerFilter: this.accountManagerFilter,
        };
        localStorage.setItem('dashboardFilters', JSON.stringify(dashboardFilters));
      }
    },
    refreshTable() {
      this.getAllCampaigns();
    },
  },
  destroyed() {
    this.saveFilters();
  },
  computed: {
    ...mapState('campaigns', [
      'campaigns',
      'headers',
      'isLoading',
      'currencies',
    ]),
    ...mapState('account', {
      accountAdvertiserId: 'advertiserId',
      features: 'features',
    }),
    ...mapGetters('account', [
      accountTypes.GET_IS_MANAGED_ACCOUNT,
    ]),
    getHeaders() {
      this.assignRowDetailsColumnsWidth();
      if (this.isMyDashboard) {
        if (this.isManagedCampaigns) {
          return [
            ...this.campaignListHeaders,
            ...this.suppressedList,
            ...this.managedCampaignsList,
            ...this.actionsHeader,
          ];
        }
        return [
          ...this.campaignListHeaders,
          ...this.suppressedList,
          ...this.actionsHeader,
        ];
      }

      return [
        ...this.campaignListHeaders,
        ...this.suppressedList,
        ...this.actionsHeader,
      ];
    },
    isNotSearching() {
      return Object.values(this.filters).every((value) => {
        if (value instanceof Array) return value.length === 0;
        return !value;
      });
    },
    filters() {
      return {
        search: this.campaignSearch,
        states: this.statesFilters,
        advertiserId: this.advertiserFilter,
        accountManagerId: this.accountManagerFilter,
      };
    },
    dashboardName() {
      return this.isMyDashboard ? 'dashboard' : 'selfServiceDashboard';
    },
    routeName() {
      return this.$route.name;
    },
  },
  async mounted() {
    this.FETCH_CAMPAIGN_STATES()
      .then((response) => {
        const states = [];
        response.forEach((state) => {
          const stateText = state.charAt(0).toUpperCase() + state.slice(1).toLowerCase();
          states.push({ id: state, name: stateText });
        });
        this.stateOptions = states;
      });

    const {
      pageOffset,
      currentPage,
      sortBy,
      sortDesc,
      search,
    } = this.$route.query;
    this.pageOffset = parseInt(pageOffset, 10) || 1;
    this.currentPage = parseInt(currentPage, 10) || 1;
    this.sortBy = sortBy || '';
    this.sortDesc = (sortDesc) ? JSON.parse(sortDesc) : false;
    this.campaignSearch = search || '';
    if (this.currencies.length === 0) this.FETCH_CURRENCIES();
    this.isMyDashboard = this.$route.meta.type === 'MY_DASHBOARD';

    const params = this.isMyDashboard ? undefined : false;
    this.isManagedCampaigns = (this.features) ? this.features.includes('MANAGED_CAMPAIGNS') : false;
    this.existingAdvertisers = await this.FETCH_EXISTING_ADVERTISERS(params);
    this.createAdvertisersList();
    this.selectFilters();
    this.isComponentLoading = false;

    await this.getAllCampaigns();
  },
  watch: {
    currentPage(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.pageOffset = this.calculatePageOffset(this.currentPage, this.perPageSelected);
        this.getAllCampaigns();
      }
    },
    perPageSelected(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.pageOffset = this.calculatePageOffset(this.currentPage, oldVal);
        this.getAllCampaigns();
      }
    },
    sortBy(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.getAllCampaigns();
      }
    },
    sortDesc(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.getAllCampaigns();
      }
    },
    features() {
      this.isManagedCampaigns = this.features.includes('MANAGED_CAMPAIGNS');
      if (this.isManagedCampaigns) this.createAccountManagerList();
    },
    campaignSearch(newVal, oldVal) {
      if (newVal !== oldVal && newVal === '') this.refreshTable();
    },
    routeName(newVal, oldVal) {
      if (!['dashboard', 'selfServiceDashboard'].includes(newVal)) return;
      if (newVal !== oldVal) {
        this.assignURLToCurrentTab(this.$router.resolve({ name: this.routeName }).href);
      }
    },
  },
};
</script>

<style lang="scss">
.page-header {
  // background: #3499ad;
  margin: 0;
}
.nav-tabs {
  border: 0;
}
.input-pills {
  input {
    background: #ffffff50;
    color: #ffffff;
    border-radius: 18px;
    border: 1px solid #fff;
    opacity: 1;
    &::-webkit-input-placeholder {
      /* Chrome/Opera/Safari */
      color: #ffffff;
    }
  }
  .input-icon-addon {
    i {
      color: #ffffff;
    }
  }
}
.budget-cell {
  min-width: 100px;
}
.pulse {
  box-shadow: 0 0 0 rgba(94, 186, 0, 0.4);
  animation: pulse 2s infinite;
}
@-webkit-keyframes pulse {
  0% {
    -webkit-box-shadow: 0 0 0 0 rgba(94, 186, 0, 0.4);
  }
  70% {
    -webkit-box-shadow: 0 0 0 10px rgba(94, 186, 0, 0);
  }
  100% {
    -webkit-box-shadow: 0 0 0 0 rgba(94, 186, 0, 0);
  }
}
@keyframes pulse {
  0% {
    -moz-box-shadow: 0 0 0 0 rgba(94, 186, 0, 0.4);
    box-shadow: 0 0 0 0 rgba(94, 186, 0, 0.4);
  }
  70% {
    -moz-box-shadow: 0 0 0 10px rgba(94, 186, 0, 0);
    box-shadow: 0 0 0 10px rgba(94, 186, 0, 0);
  }
  100% {
    -moz-box-shadow: 0 0 0 0 rgba(94, 186, 0, 0);
    box-shadow: 0 0 0 0 rgba(94, 186, 0, 0);
  }
}

.b-table-details {
  display: table-row;
}

.intro-message {
  font-size: 1.5rem;
}
.full-width {
  width: 100% !important;
}
.custom-height-dropdown {
  height: 33px;
}

#expand-column {
  width: 64px;
}

</style>
